import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Icon, type IconName } from "./Icon";

// ---------------------------------------------------------------------------
// Card — bounded surface with subtle border. Linear-style.
// ---------------------------------------------------------------------------

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Padding preset. */
  padding?: "none" | "sm" | "md" | "lg";
  /** Use as <button>? Adds hover state. */
  interactive?: boolean;
}

const cardPadding = {
  none: "",
  sm: "p-3",
  md: "p-4",
  lg: "p-6",
} as const;

export function Card({ className, padding = "md", interactive, ...props }: CardProps) {
  return (
    <div
      className={cn(
        "rounded-lg border border-ink-200 bg-surface shadow-card",
        cardPadding[padding],
        interactive && "cursor-pointer transition-colors hover:border-ink-300 hover:bg-ink-50/50",
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("mb-4 flex items-start justify-between gap-3", className)} {...props} />
  );
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn("font-semibold text-base text-ink-900", className)} {...props} />;
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("mt-0.5 text-ink-500 text-sm", className)} {...props} />;
}

// ---------------------------------------------------------------------------
// Stack — vertical layout with consistent spacing
// ---------------------------------------------------------------------------

const stackGap = {
  0: "gap-0",
  1: "gap-1",
  2: "gap-2",
  3: "gap-3",
  4: "gap-4",
  5: "gap-5",
  6: "gap-6",
  8: "gap-8",
} as const;

export interface StackProps extends HTMLAttributes<HTMLDivElement> {
  gap?: keyof typeof stackGap;
}

export function Stack({ className, gap = 4, ...props }: StackProps) {
  return <div className={cn("flex flex-col", stackGap[gap], className)} {...props} />;
}

// ---------------------------------------------------------------------------
// Row — horizontal layout
// ---------------------------------------------------------------------------

export interface RowProps extends HTMLAttributes<HTMLDivElement> {
  gap?: keyof typeof stackGap;
  /** Center cross-axis (default) or align top/bottom. */
  align?: "start" | "center" | "end" | "baseline";
  /** Justify content along main axis. */
  justify?: "start" | "center" | "end" | "between" | "around";
  wrap?: boolean;
}

const alignClass = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  baseline: "items-baseline",
} as const;

const justifyClass = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
  around: "justify-around",
} as const;

export function Row({
  className,
  gap = 3,
  align = "center",
  justify = "start",
  wrap,
  ...props
}: RowProps) {
  return (
    <div
      className={cn(
        "flex",
        stackGap[gap],
        alignClass[align],
        justifyClass[justify],
        wrap && "flex-wrap",
        className,
      )}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// Container — page max-width
// ---------------------------------------------------------------------------

export function Container({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mx-auto w-full max-w-6xl px-4 sm:px-6", className)} {...props} />;
}

// ---------------------------------------------------------------------------
// PageHeader — consistent page title + actions pattern
// ---------------------------------------------------------------------------

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Optional leading icon next to the title. */
  icon?: IconName;
  /** Back-link area or breadcrumb. Renders above the title. */
  eyebrow?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  actions,
  icon,
  eyebrow,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn("mb-6", className)}>
      {eyebrow && <div className="mb-1">{eyebrow}</div>}
      <div className="flex flex-row items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="flex min-w-0 items-center gap-2 font-semibold text-ink-900 text-xl">
            {icon && <Icon name={icon} size="md" className="text-ink-700" />}
            <span className="truncate">{title}</span>
          </h1>
          {description && <p className="mt-1 text-ink-500 text-sm">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Separator — horizontal divider
// ---------------------------------------------------------------------------

export function Separator({ className }: { className?: string }) {
  return <hr className={cn("h-px border-0 bg-ink-200", className)} />;
}
